import type { components } from "@/api-schema";
import { CompanyIdentifierSystem } from "@/api-schema-enums";
import { normalizePositiveSafeIntegerIdentifier } from "@/common/helpers/normalize-numeric-identifier";
import { z } from "zod";

type Company = Pick<components["schemas"]["Company"], "companyIdentifiers" | "uuid">;
export type CompanyIdentifier = components["schemas"]["CompanyIdentifier"];
export type CompanyIdentifierDto = components["schemas"]["CompanyIdentifierDto"];

const optionalIdentifierSchema = z.string().trim();

export const companyIdentifierFormSchema = z.object({
  v1: optionalIdentifierSchema.refine(
    (value) => value === "" || normalizeCompanyV1Identifier(value) !== undefined,
    "masterdata:CompanyV1IdentifierMustBeInteger",
  ),
  sap: optionalIdentifierSchema.refine(
    (value) => value === "" || normalizeCompanySapIdentifier(value) !== undefined,
    "masterdata:CompanySapPrefixInvalid",
  ),
});

export type CompanyIdentifierFormValues = z.infer<typeof companyIdentifierFormSchema>;

export const companyIdentifierFormDefaultValues: CompanyIdentifierFormValues = {
  v1: "",
  sap: "",
};

export function normalizeCompanyV1Identifier(identifier: string): string | undefined {
  return normalizePositiveSafeIntegerIdentifier(identifier);
}

export function normalizeCompanySapIdentifier(identifier: string): string | undefined {
  const value = identifier.trim();
  if (!/^\d+$/.test(value)) return undefined;

  const parsedValue = Number(value);
  if (!Number.isSafeInteger(parsedValue) || parsedValue < 10 || parsedValue > 99) return undefined;

  return parsedValue.toString();
}

export function normalizeCompanyIdentifier(type: CompanyIdentifierSystem, identifier: string): string | undefined {
  if (type === CompanyIdentifierSystem.V1) return normalizeCompanyV1Identifier(identifier);
  if (type === CompanyIdentifierSystem.Sap) return normalizeCompanySapIdentifier(identifier);

  const value = identifier.trim();
  return value || undefined;
}

export function getCompanyIdentifier(
  company: Pick<Company, "companyIdentifiers">,
  type: CompanyIdentifierSystem,
): string | undefined {
  return company.companyIdentifiers?.find((identifier) => identifier.type === type)?.identifier;
}

export function getCompanyV1Identifier(company: Pick<Company, "companyIdentifiers">): string | undefined {
  return getCompanyIdentifier(company, CompanyIdentifierSystem.V1);
}

export function getCompanySapIdentifier(company: Pick<Company, "companyIdentifiers">): string | undefined {
  return getCompanyIdentifier(company, CompanyIdentifierSystem.Sap);
}

export function isOperationalCustomerCompany(company: Pick<Company, "companyIdentifiers">): boolean {
  return normalizeCompanySapIdentifier(getCompanySapIdentifier(company) ?? "") !== "10";
}

export function getCompanyIdentifierKey(type: CompanyIdentifierSystem, identifier: string): string | undefined {
  const normalizedIdentifier = normalizeCompanyIdentifier(type, identifier);
  return normalizedIdentifier === undefined ? undefined : `${type}\u0000${normalizedIdentifier}`;
}

export function isDuplicateCompanyIdentifier(
  duplicateKeys: ReadonlySet<string>,
  type: CompanyIdentifierSystem,
  identifier: string,
): boolean {
  const key = getCompanyIdentifierKey(type, identifier);
  return key !== undefined && duplicateKeys.has(key);
}

export function toCompanyIdentifierFormValues(
  companyIdentifiers?: ReadonlyArray<Pick<CompanyIdentifier, "type" | "identifier">>,
): CompanyIdentifierFormValues {
  const getIdentifier = (type: CompanyIdentifierSystem) =>
    companyIdentifiers?.find((identifier) => identifier.type === type)?.identifier ?? "";

  return {
    v1: getIdentifier(CompanyIdentifierSystem.V1),
    sap: getIdentifier(CompanyIdentifierSystem.Sap),
  };
}

export function toCompanyIdentifierDtos(values: CompanyIdentifierFormValues): CompanyIdentifierDto[] {
  const identifiers: CompanyIdentifierDto[] = [];

  const appendIdentifier = (type: CompanyIdentifierSystem, identifier: string) => {
    const normalizedIdentifier = normalizeCompanyIdentifier(type, identifier);
    if (normalizedIdentifier !== undefined) identifiers.push({ type, identifier: normalizedIdentifier });
  };

  appendIdentifier(CompanyIdentifierSystem.V1, values.v1);
  appendIdentifier(CompanyIdentifierSystem.Sap, values.sap);

  return identifiers;
}

export function getDuplicateCompanyIdentifierKeys(
  companies: ReadonlyArray<Company> | undefined,
  values: CompanyIdentifierFormValues,
  excludeCompanyUuid?: string,
): Set<string> {
  if (!companies) return new Set();

  const existingKeys = new Set(
    companies
      .filter((company) => company.uuid !== excludeCompanyUuid)
      .flatMap((company) => company.companyIdentifiers ?? [])
      .filter(
        (identifier) =>
          identifier.type === CompanyIdentifierSystem.V1 ||
          (identifier.type === CompanyIdentifierSystem.Sap &&
            normalizeCompanySapIdentifier(identifier.identifier) === "10"),
      )
      .map((identifier) => getCompanyIdentifierKey(identifier.type, identifier.identifier))
      .filter((key): key is string => key !== undefined),
  );

  return new Set(
    toCompanyIdentifierDtos(values)
      .map((identifier) => getCompanyIdentifierKey(identifier.type, identifier.identifier))
      .filter((key): key is string => key !== undefined && existingKeys.has(key)),
  );
}
